import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icons,
  iconSize,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@munifocus/ui';
import { useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  insertAtSelection,
  snippetBody,
  snippetFits,
  type NoteSnippet,
} from '@munifocus/contracts-gov';
import { useNoteSnippets } from '../lib/productivity-api';

/**
 * Remember where the caret sat in a text field, so a snippet chosen from a
 * dialog (which takes the focus away) lands where the officer was typing.
 *
 * Spread `trackProps` onto the `TextField`: selection events bubble from the
 * inner textarea to its root.
 */
export function useCaretInsert() {
  const selection = useRef<{ start: number; end: number } | null>(null);
  const track = (event: SyntheticEvent) => {
    const el = event.target as HTMLTextAreaElement | HTMLInputElement;
    if (typeof el.selectionStart === 'number') {
      selection.current = { start: el.selectionStart, end: el.selectionEnd ?? el.selectionStart };
    }
  };
  return {
    trackProps: { onSelect: track, onKeyUp: track, onClick: track },
    /** `text` with `insert` spliced in at the remembered caret (or the end). */
    insertInto(text: string, insert: string): string {
      const result = insertAtSelection(
        text,
        insert,
        selection.current?.start ?? text.length,
        selection.current?.end,
      );
      selection.current = { start: result.caret, end: result.caret };
      return result.text;
    },
  };
}

/**
 * "Insert snippet": personal and team canned text, in English or French.
 *
 * The language starts at `defaultLanguage` — the officer's own for notes, the
 * owner's for a letter — and either variant can be chosen; a snippet with only
 * one variant written inserts that one.
 */
export function SnippetPicker({
  context,
  defaultLanguage,
  onInsert,
  disabled,
}: {
  context: 'note' | 'letter';
  defaultLanguage?: 'en' | 'fr' | null;
  onInsert: (text: string) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const uiLanguage: 'en' | 'fr' = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const [language, setLanguage] = useState<'en' | 'fr'>(defaultLanguage ?? uiLanguage);
  const snippets = useNoteSnippets(context);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = (snippets.data ?? []).filter(
      (s) =>
        snippetFits(s, context) &&
        (!q ||
          s.title.toLowerCase().includes(q) ||
          (s.bodyEn ?? '').toLowerCase().includes(q) ||
          (s.bodyFr ?? '').toLowerCase().includes(q)),
    );
    return [
      { key: 'personal', items: rows.filter((s) => s.scope === 'personal') },
      { key: 'team', items: rows.filter((s) => s.scope === 'team') },
    ].filter((g) => g.items.length > 0);
  }, [snippets.data, query, context]);

  const choose = (snippet: NoteSnippet) => {
    const text = snippetBody(snippet, language);
    if (text) onInsert(text);
    setOpen(false);
    setQuery('');
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<Icons.TextQuote size={iconSize.sm} />}
        onClick={() => {
          setLanguage(defaultLanguage ?? uiLanguage);
          setOpen(true);
        }}
        disabled={disabled}
      >
        {t('gov.snippets.insert')}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="snippet-picker-title"
      >
        <DialogTitle id="snippet-picker-title">{t('gov.snippets.pickerTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                label={t('gov.snippets.search')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={language}
                onChange={(_e, next: 'en' | 'fr' | null) => next && setLanguage(next)}
                aria-label={t('gov.snippets.language')}
              >
                <ToggleButton value="en">{t('gov.snippets.english')}</ToggleButton>
                <ToggleButton value="fr">{t('gov.snippets.french')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {snippets.isLoading && <CircularProgress size={24} />}
            {snippets.isError && (
              <Typography color="error" variant="body2">
                {t('gov.snippets.loadError')}
              </Typography>
            )}
            {snippets.data && groups.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {query ? t('gov.snippets.noMatch') : t('gov.snippets.empty')}
              </Typography>
            )}
            {groups.length > 0 && (
              <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {groups.map((group) => (
                  <Box component="li" key={group.key} sx={{ listStyle: 'none' }}>
                    <Box component="ul" sx={{ p: 0 }}>
                      <ListSubheader disableSticky>
                        {t(`gov.snippets.scope.${group.key}`)}
                      </ListSubheader>
                      {group.items.map((s) => (
                        <ListItemButton key={s.id} onClick={() => choose(s)}>
                          <ListItemText
                            primary={s.title}
                            secondary={snippetBody(s, language)}
                            slotProps={{
                              secondary: {
                                sx: {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                },
                              },
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </Box>
                  </Box>
                ))}
              </List>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button component={RouterLink} to="/settings#snippets" size="small">
            {t('gov.snippets.manage')}
          </Button>
          <Button onClick={() => setOpen(false)}>{t('common.actions.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
